import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HrDashboardLayout from '../components/layouts/HrDashboardLayout';
import Icon from '../components/atoms/Icon';
import Button from '../components/atoms/Button';
import DocumentSelectionModal from '../components/molecules/DocumentSelectionModal';
import SessionManager from '../utils/sessionManager';
import { getApplications, updateApplicationStatus, updateInterviewDetails, requestDocumentVerification } from '../services/api';

const HrApplicationsPage = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showActionsMenu, setShowActionsMenu] = useState(null);
  const [interviewModalOpen, setInterviewModalOpen] = useState(false);
  const [interviewForm, setInterviewForm] = useState({
    date: '',
    startTime: '',
    timezone: 'Asia/Kolkata',
    meetingLink: '',
    meetingPlatform: 'Google Meet',
    panel: '',
    notes: '',
    location: '',
  });
  const [interviewSaving, setInterviewSaving] = useState(false);
  const [interviewError, setInterviewError] = useState('');
  const [documentRequestLoading, setDocumentRequestLoading] = useState(null);
  const [documentModalOpen, setDocumentModalOpen] = useState(false);
  const [selectedApplicationForDocs, setSelectedApplicationForDocs] = useState(null);

  // Get HR company name from session
  const getHrCompanyName = () => {
    SessionManager.migrateLegacySession();
    const hrSession = SessionManager.getSession('hr');
    return hrSession?.company || hrSession?.profile?.company_name || 'Company';
  };

  const companyName = getHrCompanyName();

  // Check HR authentication
  useEffect(() => {
    const isLoggedInAsHr = SessionManager.isLoggedIn('hr');
    const currentSession = SessionManager.getCurrentSession();
    
    if (!isLoggedInAsHr || (currentSession && currentSession.role !== 'hr')) {
      navigate('/hr_login');
      return;
    }
    
    loadApplications();
  }, [navigate]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const data = await getApplications({ company: companyName });
      
      // Filter to show only applications with assessment scores (ATS score or interview score)
      const applicationsWithScores = data.filter(app => {
        return app.ats_score !== null && app.ats_score !== undefined && app.ats_score > 0;
      });
      
      console.log(`Loaded ${data.length} total applications, ${applicationsWithScores.length} with scores`);
      setApplications(applicationsWithScores);
    } catch (error) {
      console.error('Error loading applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const pipelineStages = [
    { status: 'applied', label: 'Applied', color: 'bg-gray-100 text-gray-700', icon: 'description' },
    { status: 'review', label: 'Under Review', color: 'bg-gray-200 text-gray-800', icon: 'rate_review' },
    { status: 'interview', label: 'Interview', color: 'bg-gray-300 text-gray-900', icon: 'event' },
    { status: 'offer', label: 'Offer', color: 'bg-green-100 text-green-700', icon: 'check_circle' },
    { status: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-700', icon: 'cancel' },
  ];

  const getApplicationsByStatus = (status) => {
    return applications.filter(app => app.status === status);
  };

  const filteredApplications = filter === 'all' 
    ? applications 
    : applications.filter(app => app.status === filter);

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      await updateApplicationStatus(applicationId, newStatus);
      setShowActionsMenu(null);
      loadApplications();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleMoveToInterview = async (app) => {
    setSelectedApplication(app);
    setInterviewModalOpen(true);
    setShowActionsMenu(null);
    await handleStatusChange(app.id, 'interview');
  };

  const handleSendOfferMail = (application) => {
    if (!application?.candidate_email) return;
    
    const subject = `Job Offer - ${application.job_title || 'Position'} at ${companyName}`;
    const body = `Hi ${application.candidate_name},

Congratulations! We are pleased to offer you the position of ${application.job_title || 'the role'} at ${companyName}.

We were impressed with your qualifications and believe you would be a great addition to our team.

Please reply to this email to discuss the next steps, including compensation, benefits, and start date.

We look forward to having you on board!

Best regards,
${companyName} Hiring Team`;

    window.location.href = `mailto:${application.candidate_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleSendRejectionMail = (application) => {
    if (!application?.candidate_email) return;
    
    const subject = `Application Update - ${application.job_title || 'Position'} at ${companyName}`;
    const body = `Hi ${application.candidate_name},

Thank you for your interest in the ${application.job_title || 'position'} at ${companyName} and for taking the time to interview with us.

After careful consideration, we have decided to move forward with other candidates whose qualifications more closely match our current needs.

We appreciate your interest in ${companyName} and encourage you to apply for future opportunities that match your skills and experience.

We wish you all the best in your job search.

Best regards,
${companyName} Hiring Team`;

    window.location.href = `mailto:${application.candidate_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleMoveToOffer = async (app) => {
    if (window.confirm('Send offer to this candidate?')) {
      await handleStatusChange(app.id, 'offer');
      handleSendOfferMail(app);
    }
  };

  const handleReject = async (app) => {
    if (window.confirm('Reject this application?')) {
      await handleStatusChange(app.id, 'rejected');
      handleSendRejectionMail(app);
    }
  };

  const handleOpenChat = (app) => {
    navigate('/hr/messages', { state: { selectedApplication: app } });
    setShowActionsMenu(null);
  };

  const handleRequestDocuments = (app) => {
    setSelectedApplicationForDocs(app);
    setDocumentModalOpen(true);
    setShowActionsMenu(null);
  };

  const handleDocumentSelectionConfirm = async (selectedDocuments) => {
    if (!selectedApplicationForDocs) return;
    
    try {
      setDocumentRequestLoading(selectedApplicationForDocs.id);
      
      const result = await requestDocumentVerification(selectedApplicationForDocs.id, {
        candidate_id: selectedApplicationForDocs.candidate_id,
        candidate_name: selectedApplicationForDocs.candidate_name,
        candidate_email: selectedApplicationForDocs.candidate_email,
        required_documents: selectedDocuments
      });
      
      if (result.success) {
        alert(`Document verification request sent successfully! ${selectedDocuments.length} document types requested.`);
        loadApplications(); // Refresh the applications
        setDocumentModalOpen(false);
        setSelectedApplicationForDocs(null);
      } else {
        alert(result.message || 'Failed to send document verification request');
      }
    } catch (error) {
      console.error('Error requesting documents:', error);
      alert('Failed to send document verification request');
    } finally {
      setDocumentRequestLoading(null);
    }
  };

  const handleDocumentModalClose = () => {
    setDocumentModalOpen(false);
    setSelectedApplicationForDocs(null);
  };

  const formatInterviewSlot = (details) => {
    if (!details?.date) return 'To be decided';
    const date = new Date(details.date);
    const dateStr = date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    const timeStr = details.start_time || '';
    return `${dateStr} at ${timeStr} (${details.timezone || 'IST'})`;
  };

  const handleSendInviteMail = (application) => {
    if (!application?.candidate_email) return;
    if (!application?.interview_details?.meeting_link) {
      alert('Please add the meeting link before sending an invite.');
      return;
    }
    
    const details = application.interview_details;
    const scheduleText = formatInterviewSlot(details);
    const linkLine = details?.meeting_link ? `Meeting link: ${details.meeting_link}` : '';
    const panelLine = details?.panel && details.panel.length
      ? `Panel: ${details.panel.join(', ')}`
      : '';
    
    const subject = `Interview Schedule - ${application.job_title || 'Your application'}`;
    const body = `Hi ${application.candidate_name},

Your interview for ${application.job_title || 'the role'} at ${companyName} is scheduled on ${scheduleText}.
${linkLine}
${panelLine}

Please reply to confirm or reschedule if needed.

Thanks,
${companyName} hiring team`;

    window.location.href = `mailto:${application.candidate_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleSaveInterview = async (e) => {
    e.preventDefault();
    
    if (!selectedApplication) return;
    if (!interviewForm.date || !interviewForm.startTime || !interviewForm.meetingLink) {
      setInterviewError('Please provide date, time, and meeting link.');
      return;
    }
    
    setInterviewSaving(true);
    setInterviewError('');
    
    try {
      const payload = {
        date: interviewForm.date,
        start_time: interviewForm.startTime,
        timezone: interviewForm.timezone,
        meeting_link: interviewForm.meetingLink,
        meeting_platform: interviewForm.meetingPlatform,
        panel: interviewForm.panel
          ? interviewForm.panel.split(',').map((item) => item.trim()).filter(Boolean)
          : [],
        notes: interviewForm.notes || null,
        location: interviewForm.location || null,
      };
      
      const updatedApp = await updateInterviewDetails(selectedApplication.id, payload);
      
      if (updatedApp) {
        setApplications(prev =>
          prev.map(app => (app.id === updatedApp.id ? updatedApp : app))
        );
        
        // Close modal and reset form
        setInterviewModalOpen(false);
        setInterviewForm({
          date: '',
          startTime: '',
          timezone: 'Asia/Kolkata',
          meetingLink: '',
          meetingPlatform: 'Google Meet',
          panel: '',
          notes: '',
          location: '',
        });
        
        // Send email invitation
        handleSendInviteMail(updatedApp);
      }
    } catch (error) {
      setInterviewError(error.message || 'Failed to save interview details.');
    } finally {
      setInterviewSaving(false);
    }
  };

  if (loading) {
    return (
      <HrDashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </HrDashboardLayout>
    );
  }

  return (
    <HrDashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Applications</h1>
            <p className="text-gray-600 mt-1">Manage assessed candidate applications and pipeline</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Showing candidates with assessment scores</p>
            <p className="text-xs text-gray-400">ATS scores from resume analysis</p>
          </div>
        </div>

        {/* Pipeline Overview */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {pipelineStages.map((stage) => {
            const count = getApplicationsByStatus(stage.status).length;
            return (
              <div
                key={stage.status}
                onClick={() => setFilter(stage.status)}
                className={`bg-white rounded-lg shadow-sm p-6 border-2 cursor-pointer transition-all ${
                  filter === stage.status ? 'border-blue-600' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stage.color}`}>
                    <Icon name={stage.icon} size={20} />
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{count}</span>
                </div>
                <p className="text-sm font-medium text-gray-700">{stage.label}</p>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Applications ({applications.length})
            </button>
            {pipelineStages.map((stage) => (
              <button
                key={stage.status}
                onClick={() => setFilter(stage.status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === stage.status ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {stage.label} ({getApplicationsByStatus(stage.status).length})
              </button>
            ))}
          </div>
        </div>

        {/* Applications List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {filteredApplications.length === 0 ? (
            <div className="p-12 text-center">
              <Icon name="assessment" size={48} className="text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No assessed applications found</h3>
              <p className="text-gray-600">
                {filter === 'all' 
                  ? 'Applications with assessment scores will appear here'
                  : `No applications in "${pipelineStages.find(s => s.status === filter)?.label}" status with scores`
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Candidate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Job Position
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Applied Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Score
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredApplications.map((app) => {
                    const stage = pipelineStages.find(s => s.status === app.status);
                    return (
                      <tr key={app.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-semibold">
                              {app.candidate_name?.charAt(0).toUpperCase() || 'C'}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{app.candidate_name}</div>
                              <div className="text-sm text-gray-500">{app.candidate_email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{app.job_title}</div>
                          <div className="text-sm text-gray-500">{app.location}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${stage?.color || 'bg-gray-100 text-gray-700'}`}>
                            {stage?.label || app.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(app.applied_at).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col">
                            {/* ATS Score */}
                            {app.ats_score !== null && app.ats_score !== undefined ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">ATS:</span>
                                <span className={`text-sm font-semibold ${
                                  app.ats_score >= 70 ? 'text-green-600' :
                                  app.ats_score >= 50 ? 'text-yellow-600' :
                                  'text-red-600'
                                }`}>
                                  {Math.round(app.ats_score)}%
                                </span>
                              </div>
                            ) : null}
                            
                            {/* Interview Score */}
                            {app.interview_details?.interview_score !== null && app.interview_details?.interview_score !== undefined ? (
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-gray-500">Interview:</span>
                                <span className={`text-sm font-semibold ${
                                  app.interview_details.interview_score >= 70 ? 'text-green-600' :
                                  app.interview_details.interview_score >= 50 ? 'text-yellow-600' :
                                  'text-red-600'
                                }`}>
                                  {Math.round(app.interview_details.interview_score)}%
                                </span>
                              </div>
                            ) : null}
                            
                            {/* No scores available */}
                            {(!app.ats_score && !app.interview_details?.interview_score) && (
                              <span className="text-sm text-gray-400">No scores</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => navigate(`/hr/candidate-profile/${app.candidate_id}`)}
                              className="text-blue-600 hover:text-blue-900"
                            >
                              View Profile
                            </button>
                            <div className="relative">
                              <button
                                onClick={() => setShowActionsMenu(showActionsMenu === app.id ? null : app.id)}
                                className="p-2 rounded-lg hover:bg-gray-100"
                              >
                                <Icon name="more_vert" size={20} className="text-gray-600" />
                              </button>
                              
                              {showActionsMenu === app.id && (
                                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                                  <div className="py-1">
                                    <button
                                      onClick={() => handleMoveToInterview(app)}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                    >
                                      <Icon name="event" size={16} className="text-purple-600" />
                                      Move to Interview
                                    </button>
                                    <button
                                      onClick={() => handleMoveToOffer(app)}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                    >
                                      <Icon name="check_circle" size={16} className="text-green-600" />
                                      Move to Offer
                                    </button>
                                    {app.status === 'offer' && (
                                      <button
                                        onClick={() => handleRequestDocuments(app)}
                                        disabled={documentRequestLoading === app.id}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50"
                                      >
                                        <Icon name="description" size={16} className="text-orange-600" />
                                        {documentRequestLoading === app.id ? 'Requesting...' : 'Request Documents'}
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleReject(app)}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                    >
                                      <Icon name="cancel" size={16} className="text-red-600" />
                                      Move to Reject
                                    </button>
                                    <div className="border-t border-gray-200 my-1"></div>
                                    <button
                                      onClick={() => handleOpenChat(app)}
                                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                    >
                                      <Icon name="chat" size={16} className="text-blue-600" />
                                      Chat
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Interview Schedule Modal */}
      {interviewModalOpen && selectedApplication && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setInterviewModalOpen(false)}>
          <div 
            className="relative w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Schedule Interview</h2>
              <button
                onClick={() => setInterviewModalOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <Icon name="close" size={24} />
              </button>
            </div>

            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">
                <strong>Candidate:</strong> {selectedApplication.candidate_name}
              </p>
              <p className="text-sm text-gray-700">
                <strong>Position:</strong> {selectedApplication.job_title}
              </p>
            </div>

            {interviewError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {interviewError}
              </div>
            )}

            <form onSubmit={handleSaveInterview} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Interview Date</label>
                  <input
                    type="date"
                    value={interviewForm.date}
                    onChange={(e) => setInterviewForm({ ...interviewForm, date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Start Time</label>
                  <input
                    type="time"
                    value={interviewForm.startTime}
                    onChange={(e) => setInterviewForm({ ...interviewForm, startTime: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Platform</label>
                  <select
                    value={interviewForm.meetingPlatform}
                    onChange={(e) => setInterviewForm({ ...interviewForm, meetingPlatform: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Google Meet">Google Meet</option>
                    <option value="Zoom">Zoom</option>
                    <option value="Microsoft Teams">Microsoft Teams</option>
                    <option value="In-person">In-person</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                  <select
                    value={interviewForm.timezone}
                    onChange={(e) => setInterviewForm({ ...interviewForm, timezone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                    <option value="America/New_York">America/New_York (EST)</option>
                    <option value="Europe/London">Europe/London (GMT)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Link</label>
                <input
                  type="url"
                  value={interviewForm.meetingLink}
                  onChange={(e) => setInterviewForm({ ...interviewForm, meetingLink: e.target.value })}
                  placeholder="https://meet.google.com/..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Interview Panel</label>
                <input
                  type="text"
                  value={interviewForm.panel}
                  onChange={(e) => setInterviewForm({ ...interviewForm, panel: e.target.value })}
                  placeholder="e.g., John Doe, Jane Smith"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location (for in-person)</label>
                <input
                  type="text"
                  value={interviewForm.location}
                  onChange={(e) => setInterviewForm({ ...interviewForm, location: e.target.value })}
                  placeholder="Office address or meeting room"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={interviewForm.notes}
                  onChange={(e) => setInterviewForm({ ...interviewForm, notes: e.target.value })}
                  placeholder="Additional notes or instructions..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center gap-4 pt-4 border-t">
                <Button type="submit" disabled={interviewSaving}>
                  {interviewSaving ? 'Saving...' : 'Schedule & Send Email'}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setInterviewModalOpen(false);
                    setInterviewError('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Document Selection Modal */}
      <DocumentSelectionModal
        isOpen={documentModalOpen}
        onClose={handleDocumentModalClose}
        onConfirm={handleDocumentSelectionConfirm}
        candidateName={selectedApplicationForDocs?.candidate_name}
        loading={documentRequestLoading !== null}
      />
    </HrDashboardLayout>
  );
};

export default HrApplicationsPage;
